import { useState } from 'react';
import * as Yup from 'yup';
import Button from '../../../../components/form-control/Button';
import SelectDropdown from '../../../../components/form-control/SelectDropdown';
import { Policy } from '../../../../models/policy/Policy';
import Input from '../../../../components/form-control/Input';
import { Formik } from 'formik';
import {
  getFormattedPhoneNumber,
  getPhoneNumberFromShortCodeFormat,
  showToast
} from '../../../../utils/miscFunctions';
import { adminRequestFeedbackFromCustomer } from '../../../../api/requests';

type RequestReviewModalProps = {
  policy: Policy;
  onClose: () => void;
  onProceed?: (url: string) => void;
};

const RequestReviewModal = ({ policy, onClose, onProceed }: RequestReviewModalProps) => {
  const [loading, setLoading] = useState<boolean>(false);

  return (
    <div className="w-full">

      <div className="px-5">
        <Formik
          initialValues={{
            channel: '',
            email: policy?.applicant?.email,
            phone: getPhoneNumberFromShortCodeFormat(policy?.applicant?.phoneNumber ?? '')
          }}
          validateOnChange={false}
          validateOnBlur={true}
          validationSchema={Yup.object({
            channel: Yup.string().trim().required('Channel is required.')
          })}
          onSubmit={async (values) => {
            setLoading(true);
            const phone = getFormattedPhoneNumber(values.phone);
            const identifier =
              values.channel === 'sms' || values.channel === 'whatsapp' ? phone : values.email;

            // Ensure identifier is valid
            if (!identifier) {
              showToast.error('Please provide a valid phone number or email.');
              setLoading(false);
              return;
            }

            try {
              const response = await adminRequestFeedbackFromCustomer(policy?.ref, values.channel, identifier);

              if (response) {
                showToast.success(response?.message ?? 'Feedback link shared successfully.', );
                onProceed?.(response?.data);
              }
            } catch (error: any) {
              showToast.error(error?.description);
            } finally {
              setLoading(false);
            }
          }}
        >
          {({ values, errors, handleSubmit, handleChange, setFieldValue, handleBlur, touched }) => (
            <form className="" onSubmit={handleSubmit}>
              <div className="mt-5">
                <SelectDropdown
                  label="Channel"
                  name="channel"
                  listSelectedValue={values.channel}
                  list={[
                    { name: 'Email', value: 'email' },
                    { name: 'SMS', value: 'sms' },
                    { name: 'WhatsApp', value: 'whatsapp' }
                  ]}
                  onChange={(val: string | null) => setFieldValue('channel', val)}
                  onBlur={handleBlur}
                  placeholder="Select channel of transfer"
                  error={touched.channel ? errors.channel : undefined}
                  helperText={touched.channel ? errors.channel : undefined}
                  required
                  wrapperClasses="w-full"
                />
              </div>

              <div className="mt-5">
                {values.channel == 'email' && (
                  <Input
                    name="email"
                    label="Email"
                    value={values.email}
                    type="text"
                    wrapperClasses=""
                    required
                    onChange={handleChange}
                    onBlur={handleChange}
                    error={errors.email}
                    helperText={errors.email}
                  />
                )}
                {(values.channel == 'sms' || values.channel == 'whatsapp') && (
                  <Input
                    name="phone"
                    label="Phone"
                    value={values.phone}
                    type="tel"
                    wrapperClasses=""
                    onChange={handleChange}
                    onBlur={handleChange}
                    error={errors.phone}
                    helperText={errors.phone}
                    required
                  />
                )}
              </div>

              <div className="w-full flex gap-4 mt-6 !mb-4">
                <Button
                  type="button"
                  label="Cancel"
                  onClick={onClose}
                  bgColor='secondary'
                  fullWidth
                />

                <Button
                  type="submit"
                  label="Submit"
                  loading={loading}
                  disabled={loading}
                  bgColor='primary'
                  fullWidth
                />
              </div>
            </form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default RequestReviewModal;
